import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { api } from '../lib/api';
import type { CRMAlert, PriorityContact } from '../types';

function formatCurrency(val?: number | null): string {
  if (!val) return '—';
  return '$' + val.toLocaleString('en-US', { maximumFractionDigits: 0 });
}

function timeAgo(date: string): string {
  const seconds = Math.floor((Date.now() - new Date(date).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return `${Math.floor(days / 30)}mo ago`;
}

function healthColor(score: number): string {
  if (score >= 70) return 'text-success';
  if (score >= 40) return 'text-warning';
  return 'text-red-600';
}

function healthBg(score: number): string {
  if (score >= 70) return 'bg-success-wash';
  if (score >= 40) return 'bg-warning-wash';
  return 'bg-red-100';
}

function priorityBar(score: number): string {
  if (score >= 70) return 'bg-success';
  if (score >= 40) return 'bg-warning';
  return 'bg-red-500';
}

export default function CRMIntelligence() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<'alerts' | 'priority' | 'pipeline' | 'at-risk'>('alerts');

  // Alerts
  const [alerts, setAlerts] = useState<CRMAlert[]>([]);
  const [alertsTotal, setAlertsTotal] = useState(0);
  const [alertFilter, setAlertFilter] = useState('');
  const [recomputing, setRecomputing] = useState(false);

  // Priority
  const [leaders, setLeaders] = useState<PriorityContact[]>([]);

  // Pipeline
  const [pipeline, setPipeline] = useState<any>(null);

  // At-risk
  const [atRisk, setAtRisk] = useState<any[]>([]);

  const loadAlerts = useCallback(async () => {
    try {
      const params: Record<string, string> = {};
      if (alertFilter) params.severity = alertFilter;
      const res = await api.getCRMAlerts(params);
      setAlerts(res.alerts);
      setAlertsTotal(res.total);
    } catch (err) {
      console.error('Failed to load alerts:', err);
    }
  }, [alertFilter]);

  useEffect(() => { if (tab === 'alerts') loadAlerts(); }, [tab, loadAlerts]);

  useEffect(() => {
    if (tab === 'priority') {
      api.getPriorityLeaderboard(30).then((r) => setLeaders(r.contacts || [])).catch(console.error);
    }
  }, [tab]);

  useEffect(() => {
    if (tab === 'pipeline') {
      api.getPipelineHealth().then((r) => setPipeline(r)).catch(console.error);
    }
  }, [tab]);

  useEffect(() => {
    if (tab === 'at-risk') {
      api.getAtRiskDeals().then((r) => setAtRisk(r.deals || [])).catch(console.error);
    }
  }, [tab]);

  const handleRecompute = async () => {
    setRecomputing(true);
    try {
      await api.recomputeCRMScores();
      loadAlerts();
      if (tab === 'priority') {
        api.getPriorityLeaderboard(30).then((r) => setLeaders(r.contacts || []));
      }
    } catch (err) {
      console.error('Recompute failed:', err);
    } finally {
      setRecomputing(false);
    }
  };

  const handleDismiss = async (id: string) => {
    try {
      await api.dismissCRMAlert(id);
      setAlerts((prev) => prev.filter((a) => a.id !== id));
      setAlertsTotal((t) => t - 1);
    } catch (err) {
      console.error('Dismiss failed:', err);
    }
  };

  const tabs = [
    { key: 'alerts' as const, label: 'Alerts', count: alertsTotal },
    { key: 'priority' as const, label: 'Priority' },
    { key: 'pipeline' as const, label: 'Pipeline Health' },
    { key: 'at-risk' as const, label: 'At-Risk Deals' },
  ];

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-display text-[28px] font-semibold uppercase leading-[1.06] tracking-[-.01em] text-gray-900 sm:text-[32px]">CRM Intelligence</h1>
            <p className="text-sm text-gray-500 mt-1">Priority scoring, alerts, and pipeline health analysis</p>
          </div>
          <button
            onClick={handleRecompute}
            disabled={recomputing}
            className="px-4 py-2 bg-primary-500 text-white rounded-lg text-sm font-medium hover:bg-primary-600 disabled:opacity-50 flex items-center gap-2"
          >
            {recomputing ? (
              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            ) : (
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            )}
            Recompute Scores
          </button>
        </div>

        <div className="border-b border-gray-200">
          <nav className="flex gap-6">
            {tabs.map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`pb-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                  tab === t.key ? 'border-primary-500 text-primary-500' : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {t.label}
                {t.count !== undefined && t.count > 0 && (
                  <span className="px-1.5 py-0.5 text-xs rounded-full bg-red-100 text-red-700">{t.count}</span>
                )}
              </button>
            ))}
          </nav>
        </div>

        {/* Alerts Tab */}
        {tab === 'alerts' && (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <select
                value={alertFilter}
                onChange={(e) => setAlertFilter(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
              >
                <option value="">All Severities</option>
                <option value="urgent">Urgent</option>
                <option value="warning">Warning</option>
                <option value="info">Info</option>
              </select>
              <span className="text-sm text-gray-500">{alertsTotal} alerts</span>
            </div>

            {alerts.length === 0 ? (
              <div className="bg-white rounded-xl border p-12 text-center text-gray-400">
                No active alerts. Click "Recompute Scores" to generate alerts.
              </div>
            ) : (
              <div className="space-y-3">
                {alerts.map((alert) => (
                  <div key={alert.id} className="bg-white rounded-xl border p-4 flex items-start gap-4">
                    {/* Severity icon */}
                    <div className="flex-shrink-0 mt-0.5">
                      {alert.severity === 'urgent' && (
                        <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center">
                          <svg className="w-5 h-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                          </svg>
                        </div>
                      )}
                      {alert.severity === 'warning' && (
                        <div className="w-8 h-8 rounded-full bg-warning-wash flex items-center justify-center">
                          <svg className="w-5 h-5 text-warning" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                      )}
                      {alert.severity === 'info' && (
                        <div className="w-8 h-8 rounded-full bg-info-wash flex items-center justify-center">
                          <svg className="w-5 h-5 text-info" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                      )}
                    </div>
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">{alert.title}</p>
                      {alert.description && <p className="text-sm text-gray-500 mt-0.5">{alert.description}</p>}
                      <div className="flex items-center gap-3 mt-2">
                        <span className="text-xs text-gray-400">{timeAgo(alert.created_at)}</span>
                        {alert.related_contact_id && (
                          <button onClick={() => navigate(`/crm/contacts/${alert.related_contact_id}`)} className="text-xs text-primary-500 hover:text-primary-600">
                            View Contact
                          </button>
                        )}
                        {alert.related_deal_id && (
                          <button onClick={() => navigate(`/crm/deals/${alert.related_deal_id}`)} className="text-xs text-primary-500 hover:text-primary-600">
                            View Deal
                          </button>
                        )}
                      </div>
                    </div>
                    {/* Dismiss */}
                    <button onClick={() => handleDismiss(alert.id)} className="flex-shrink-0 text-gray-400 hover:text-gray-600" title="Dismiss">
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Priority Leaderboard Tab */}
        {tab === 'priority' && (
          <div className="bg-white rounded-xl border overflow-hidden">
            {leaders.length === 0 ? (
              <div className="p-12 text-center text-gray-400">No priority scores yet. Click "Recompute Scores" to calculate.</div>
            ) : (
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-12">#</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Company</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-48">Priority Score</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Days Since Contact</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {leaders.map((c, i) => (
                    <tr key={c.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-gray-500 font-medium">{i + 1}</td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => navigate(`/crm/contacts/${c.id}`)}
                          className="text-primary-500 hover:text-primary-600 font-medium"
                        >
                          {[c.first_name, c.last_name].filter(Boolean).join(' ') || 'Unknown'}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-gray-600">{c.company || '—'}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full ${priorityBar(c.priority_score)}`}
                              style={{ width: `${c.priority_score}%` }}
                            />
                          </div>
                          <span className={`text-sm font-bold ${healthColor(c.priority_score)}`}>{c.priority_score}</span>
                        </div>
                        {c.priority_breakdown && (
                          <div className="flex gap-2 mt-1">
                            {Object.entries(c.priority_breakdown).map(([k, v]) => (
                              <span key={k} className="text-[10px] text-gray-400">{k}: {v}</span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className={`${(c.days_since_contact || 0) > 7 ? 'text-red-600 font-medium' : 'text-gray-600'}`}>
                          {c.days_since_contact ?? '—'}d
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Pipeline Health Tab */}
        {tab === 'pipeline' && pipeline && (
          <div className="space-y-6">
            {/* Summary cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white rounded-xl border p-6">
                <p className="text-xs text-gray-500 uppercase">Total Deals</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{pipeline.total_deals}</p>
              </div>
              <div className="bg-white rounded-xl border p-6">
                <p className="text-xs text-gray-500 uppercase">Total Value</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{formatCurrency(pipeline.total_value)}</p>
              </div>
              <div className="bg-white rounded-xl border p-6">
                <p className="text-xs text-gray-500 uppercase">Avg Health Score</p>
                <p className={`text-3xl font-bold mt-1 ${healthColor(pipeline.avg_health)}`}>{pipeline.avg_health}</p>
              </div>
            </div>

            {/* Stage breakdown */}
            <div className="bg-white rounded-xl border overflow-hidden">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stage</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Count</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Value</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Avg Health</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Avg Days</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-48">Distribution</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {Object.entries(pipeline.stages || {}).map(([stage, s]: [string, any]) => (
                    <tr key={stage} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-gray-900 font-medium capitalize">{stage.replace(/_/g, ' ')}</td>
                      <td className="px-4 py-3 text-center text-gray-600">{s.count}</td>
                      <td className="px-4 py-3 text-right text-gray-900">{formatCurrency(s.value)}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${healthBg(s.avg_health)} ${healthColor(s.avg_health)}`}>
                          {s.avg_health}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center text-gray-600">{s.avg_days}d</td>
                      <td className="px-4 py-3">
                        <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full ${priorityBar(s.avg_health)}`}
                            style={{ width: `${Math.max(5, (s.count / pipeline.total_deals) * 100)}%` }}
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === 'pipeline' && !pipeline && (
          <div className="bg-white rounded-xl border p-12 text-center text-gray-400">Loading pipeline health...</div>
        )}

        {/* At-Risk Deals Tab */}
        {tab === 'at-risk' && (
          <div className="space-y-4">
            {atRisk.length === 0 ? (
              <div className="bg-white rounded-xl border p-12 text-center text-gray-400">
                No at-risk deals found. All deals are healthy!
              </div>
            ) : (
              atRisk.map((deal: any) => (
                <div key={deal.id} className="bg-white rounded-xl border p-5">
                  <div className="flex items-start justify-between">
                    <div>
                      <button
                        onClick={() => navigate(`/crm/deals/${deal.id}`)}
                        className="text-base font-semibold text-primary-500 hover:text-primary-600"
                      >
                        {deal.title || 'Untitled Deal'}
                      </button>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 capitalize">
                          {(deal.stage || 'new').replace(/_/g, ' ')}
                        </span>
                        <span className="text-sm text-gray-600">{formatCurrency(deal.value)}</span>
                        <span className="text-xs text-gray-400">{deal.days_in_stage}d in stage</span>
                      </div>
                    </div>
                    <span className={`text-2xl font-bold ${healthColor(deal.health_score)}`}>
                      {deal.health_score}
                    </span>
                  </div>
                  {deal.health_flags && deal.health_flags.length > 0 && (
                    <ul className="mt-3 space-y-1">
                      {deal.health_flags.map((flag: string, i: number) => (
                        <li key={i} className="flex items-center gap-2 text-sm text-red-600">
                          <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01" />
                          </svg>
                          {flag}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
